import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';

import { QUESTION_PAGE_UI } from '../../constants/ui-strings';
import { QuestionsService } from 'src/app/services/questions.service';
import { Question } from 'src/app/model/question';
import { QUESTION_TYPES, TOAST_CONFIGURATION } from 'src/app/constants/constants';
import { QuestionRecord } from 'src/app/model/question-record';
import { TestService } from 'src/app/services/test.service';
import { MessageService } from 'primeng/api';

import { parseQuestionContent, StatementPart } from './parse-question-content';

@Component({
  selector: 'app-question-page',
  templateUrl: './question-page.component.html',
  styleUrl: './question-page.component.scss',
})
export class QuestionPageComponent implements OnInit, OnDestroy {
  questionPageUIStrings = QUESTION_PAGE_UI;
  questionType = QUESTION_TYPES;
  selectedQuestionNumber: number;
  selectedQuestion: Question;
  selectedQuestionRecord: QuestionRecord;
  isLoading: boolean = false;
  statementParts: StatementPart[] = [];
  optionParts: StatementPart[][] = [[], [], [], []];
  selectedQuestionNumberSubscription$: Subscription;
  changeQuestionRecordSubscription$: Subscription;
  submitAnswerSubscription$: Subscription;

  constructor (
    private questionsService: QuestionsService,
    private testService: TestService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.selectedQuestionNumber = this.questionsService.selectedQuestionNumber;
    this.selectedQuestion = this.questionsService.questionsList[this.selectedQuestionNumber];
    this.selectedQuestionRecord = this.questionsService
      .questionRecords[this.selectedQuestionNumber];
    this.refreshContentParts();
    this.selectedQuestionNumberSubscription$ = this.questionsService
    .selectedQuestionNumber$
    .subscribe({
      next: (value) => {
        this.selectedQuestionNumber = value;
        this.selectedQuestion = this.questionsService.questionsList[value];
        this.selectedQuestionRecord = this.questionsService.questionRecords[value];
        this.refreshContentParts();
      }
    });

    this.selectedQuestionRecord = this.questionsService
      .questionRecords[this.selectedQuestionNumber];
    this.changeQuestionRecordSubscription$ = this.questionsService
    .changeQuestionRecord$
    .subscribe({
      next: () => {
        this.selectedQuestionRecord = this.questionsService
          .questionRecords[this.selectedQuestionNumber];
      }
    });
  }

  private refreshContentParts(): void {
    this.statementParts = parseQuestionContent(this.selectedQuestion?.PROBLEM_STATEMENT);
    this.optionParts = [
      parseQuestionContent(this.selectedQuestion?.OPTION_1),
      parseQuestionContent(this.selectedQuestion?.OPTION_2),
      parseQuestionContent(this.selectedQuestion?.OPTION_3),
      parseQuestionContent(this.selectedQuestion?.OPTION_4),
    ];
  }

  ngOnDestroy(): void {
    this.selectedQuestionNumberSubscription$?.unsubscribe();
    this.submitAnswerSubscription$?.unsubscribe();
  }

  get reviewButtonLabel(): string {
    if (this.selectedQuestionRecord.isMarkedForReview) {
      return this.questionPageUIStrings.clear_review;
    }
    return this.questionPageUIStrings.review;
  }

  get clearButtonLabel(): string {
    return this.questionPageUIStrings.clear_answer;
  }

  get selectedOption(): string[] {
    return this.questionsService.temporarySelectedOption;
  }

  saveAndNext(): void {
    if (this.questionsService.temporarySelectedOption.length == 0) {
      return;
    }
    const questionId: string = this.selectedQuestion.q_id;
    const selectedOptions: string[] = this.questionsService.temporarySelectedOption;
    this.isLoading = true;
    this.testService.submitAnswer(questionId, selectedOptions).subscribe({
      next: (res) => {
        if (res.success) {
          this.messageService.add({
            severity: TOAST_CONFIGURATION.success,
            detail: res.message
          })
          this.questionsService.answerQuestion();
        } else {
          this.messageService.add({
            severity: TOAST_CONFIGURATION.info,
            detail: res.message
          })
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.messageService.add({
            severity: TOAST_CONFIGURATION.failed,
            summary: err.status,
            detail: err.message
          })
          this.isLoading = false;
      }
    })
  }

  clear(): void {
    if (!this.selectedQuestionRecord.isAnswered) {
      return;
    }
    const questionId: string = this.selectedQuestion.q_id;
    const selectedOptions: string[] = [];
    this.isLoading = true;
    this.testService.submitAnswer(questionId, selectedOptions).subscribe({
      next: (res) => {
        if (res.success) {
          this.messageService.add({
            severity: TOAST_CONFIGURATION.success,
            detail: res.message
          })
          this.questionsService.unanswerQuestion();
        } else {
          this.messageService.add({
            severity: TOAST_CONFIGURATION.info,
            detail: res.message
          })
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.messageService.add({
            severity: TOAST_CONFIGURATION.failed,
            summary: err.status,
            detail: err.message
          })
          this.isLoading = false;
      }
    })
  }

  review(): void {
    this.questionsService
    .setReviewStatus(!this.selectedQuestionRecord.isMarkedForReview);
  }

  isOptionChecked(option: string): boolean {
    return this.selectedOption.includes(option);
  }

  saveResponse(option: string): void {
    if (this.selectedQuestion.QUESTION_TYPE === QUESTION_TYPES.single_correct) {
      this.saveResponseSingleCorrect(option);
    } else if (this.selectedQuestion.QUESTION_TYPE === QUESTION_TYPES.multi_correct) {
      this.saveResponseMultipleCorrect(option);
    }
  }

  saveResponseSingleCorrect(option: string): void {
    this.questionsService.temporarySelectedOption = [option];
  }

  saveResponseMultipleCorrect(option: string): void {
    if (this.questionsService.temporarySelectedOption.includes(option)) {
      this.questionsService.temporarySelectedOption = this.questionsService
      .temporarySelectedOption.filter((markedOption) => {
        if (markedOption == option) {
          return false;
        }
        return true;
      });
    } else {
      this.questionsService.temporarySelectedOption = [
        ...this.questionsService.temporarySelectedOption, 
        option
      ];
    }
  }
}
